import type { Filter, FilterDataOption, FilterDataMultiSelect } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { getLocalStorageObject, LOCAL_STORAGE_KEY } from '@/app/components/search/utils/localStorage';
import type { TransportAirport } from '@/domainLogic/types/typesTransport.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';

const DESTINATION_AIRPORT_DEFAULT_VALUE = '0';

type CreateDestinationAirportFilterOptions = {
  selectedValues: string[];
  airports: TransportAirport[];
  translationDictionary: TranslationDictionary;
};
const createDestinationAirportFilterOptions = ({
  selectedValues,
  airports,
  translationDictionary,
}: CreateDestinationAirportFilterOptions): FilterDataOption[] => [
  {
    label: getTranslationServer('vacation.oss.filters.destination_airport.any', translationDictionary),
    selected: selectedValues.includes(DESTINATION_AIRPORT_DEFAULT_VALUE),
    value: DESTINATION_AIRPORT_DEFAULT_VALUE,
    isDefault: true,
  },
  ...airports.map((airport) => ({
    label: `${airport.cityName} (${airport.iata}) ${airport.name}`,
    selected: selectedValues.includes(airport.iata),
    value: airport.iata,
  })),
];

type CreateDestinationAirportFilters = {
  searchParams: SearchParams;
  productsData: ProductsData;
  translationDictionary: TranslationDictionary;
};
export const createDestinationAirportFilters = ({
  searchParams,
  productsData,
  translationDictionary,
}: CreateDestinationAirportFilters): Filter[] => {
  const selectedValuesFromUrl = searchParams[URLParameters.PRE_FILTER_DESTINATION_AIRPORT]?.split(',') || [];
  const selectedValues = selectedValuesFromUrl.length > 0 ? selectedValuesFromUrl : [DESTINATION_AIRPORT_DEFAULT_VALUE];

  let airports: TransportAirport[] = Array.isArray(productsData?.transport?.airports)
    ? productsData?.transport?.airports
    : [];
  if (!airports) {
    const airportsLS = getLocalStorageObject(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT);
    airports = Array.isArray(airportsLS) ? airportsLS : [];
  }
  const hasAirports = airports.length > 0;
  const destinationAirportFilter: Filter | undefined = hasAirports
    ? {
        name: FILTER_NAME.DESTINATION_AIRPORT,
        type: FILTER_TYPE.RADIO,
        data: {
          options: createDestinationAirportFilterOptions({
            selectedValues,
            airports,
            translationDictionary,
          }),
          defaultValue: DESTINATION_AIRPORT_DEFAULT_VALUE,
        } as FilterDataMultiSelect,
      }
    : undefined;

  return [...[destinationAirportFilter]].filter((f) => !!f);
};
